import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import {
  MoreHorizontal, PauseCircle, PlayCircle, Ban, ShieldOff, Trash2, RotateCw,
  AlertTriangle, Loader2,
} from 'lucide-react';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';

/**
 * Per-row dropdown of moderation actions for a customer.
 *
 *   • Suspend  / Activate         — manager+ (`is_active` toggle)
 *   • Ban      / Unban            — manager+ (`is_banned` + customer_type)
 *   • Delete   (soft / hard)      — manager+ for soft, super_admin for hard
 *   • Restore                     — super_admin (un-soft-delete)
 *
 * Disabled items are hidden based on the current `status` ("active" |
 * "suspended" | "banned" | "deleted") so the UI never offers a transition
 * the backend would refuse.
 *
 * Props:
 *   • customer       : { _id, status, full_name, ... }
 *   • currentRole    : 'super_admin' | 'manager' | ...
 *   • onChange       : called after a successful action (parent refetches)
 *   • compact        : (bool) renders just an icon-only trigger for tables
 */
export default function CustomerActionsMenu({ customer, currentRole, onChange, compact = false }) {
  const [dialog, setDialog]   = useState(null);   // { action, title, danger, requireReason }
  const [reason, setReason]   = useState('');
  const [busy, setBusy]       = useState(false);

  const status = customer.status || (
    customer.deleted_at ? 'deleted'
      : (customer.is_banned || customer.customer_type === 'blacklisted') ? 'banned'
      : (customer.is_active ? 'active' : 'suspended')
  );

  const canModerate = currentRole === 'super_admin' || currentRole === 'manager';
  const isSuperAdmin = currentRole === 'super_admin';

  const open = (action) => {
    setReason('');
    const cfg = {
      suspend:   { title: 'Suspend customer',  desc: `Temporarily disable ${customer.full_name}'s account. They won't be able to log in or check in until reactivated.`,         danger: false, requireReason: false },
      activate:  { title: 'Activate customer', desc: `Re-enable ${customer.full_name}'s account.`,                                                                                danger: false, requireReason: false },
      ban:       { title: 'Ban customer',      desc: `Permanently block ${customer.full_name}. They'll be moved to the blacklist. Reversible only via Unban.`,                    danger: true,  requireReason: true  },
      unban:     { title: 'Unban customer',    desc: `Remove ${customer.full_name} from the blacklist. Their previous tier is restored.`,                                          danger: false, requireReason: false },
      delete:    { title: 'Delete customer',   desc: `Soft-delete ${customer.full_name}. Their personal info will be anonymised but check-in history is preserved.`,             danger: true,  requireReason: false },
      hardDelete:{ title: 'Permanently delete', desc: `Remove ${customer.full_name} from the database. This cannot be undone.`,                                                   danger: true,  requireReason: true  },
      restore:   { title: 'Restore customer',  desc: `Un-delete ${customer.full_name}. PII (name/email/phone) was anonymised on deletion and cannot be recovered automatically.`, danger: false, requireReason: false },
    }[action];
    setDialog({ action, ...cfg });
  };

  const run = async () => {
    if (dialog.requireReason && !reason.trim()) {
      toast.error('A reason is required for this action.');
      return;
    }
    setBusy(true);
    try {
      const id = customer._id;
      const r  = reason.trim() || undefined;
      const map = {
        suspend:    () => adminApi.suspendCustomer(id, r),
        activate:   () => adminApi.activateCustomer(id, r),
        ban:        () => adminApi.banCustomer(id, r),
        unban:      () => adminApi.unbanCustomer(id, r),
        delete:     () => adminApi.deleteCustomer(id, { hard: false, reason: r }),
        hardDelete: () => adminApi.deleteCustomer(id, { hard: true,  reason: r }),
        restore:    () => adminApi.restoreCustomer(id, r),
      };
      await map[dialog.action]();
      const msgs = {
        suspend: 'Customer suspended', activate: 'Customer reactivated',
        ban: 'Customer banned',        unban: 'Customer unbanned',
        delete: 'Customer deleted',    hardDelete: 'Customer permanently deleted',
        restore: 'Customer restored',
      };
      toast.success(msgs[dialog.action]);
      setDialog(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (!canModerate) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {compact ? (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-[#B9B9B9] hover:text-[#F2F2F2]"
              data-testid={`customer-actions-${customer._id}`}
              onClick={e => e.stopPropagation()}
            >
              <MoreHorizontal size={15}/>
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] gap-2"
              data-testid={`customer-actions-${customer._id}`}
            >
              <MoreHorizontal size={14}/> Manage
            </Button>
          )}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-[#0F0F0F] border border-[#2A2A2A] text-[#F2F2F2] w-56"
          onClick={e => e.stopPropagation()}
        >
          {/* Active flow */}
          {status === 'active' && (
            <>
              <DropdownMenuItem onClick={() => open('suspend')} data-testid="action-suspend" className="gap-2 cursor-pointer">
                <PauseCircle size={14} className="text-[#FBB924]"/> Suspend
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => open('ban')} data-testid="action-ban" className="gap-2 cursor-pointer text-[#FF8A8A]">
                <Ban size={14}/> Ban
              </DropdownMenuItem>
            </>
          )}
          {/* Suspended flow */}
          {status === 'suspended' && (
            <>
              <DropdownMenuItem onClick={() => open('activate')} data-testid="action-activate" className="gap-2 cursor-pointer">
                <PlayCircle size={14} className="text-[#4ADE80]"/> Activate
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => open('ban')} data-testid="action-ban" className="gap-2 cursor-pointer text-[#FF8A8A]">
                <Ban size={14}/> Ban
              </DropdownMenuItem>
            </>
          )}
          {/* Banned flow */}
          {status === 'banned' && (
            <DropdownMenuItem onClick={() => open('unban')} data-testid="action-unban" className="gap-2 cursor-pointer">
              <ShieldOff size={14} className="text-[#4ADE80]"/> Unban
            </DropdownMenuItem>
          )}
          {/* Deleted flow */}
          {status === 'deleted' && isSuperAdmin && (
            <DropdownMenuItem onClick={() => open('restore')} data-testid="action-restore" className="gap-2 cursor-pointer">
              <RotateCw size={14} className="text-[#4ADE80]"/> Restore
            </DropdownMenuItem>
          )}

          {/* Delete options — separator + danger group, shown for any non-deleted */}
          {status !== 'deleted' && (
            <>
              <DropdownMenuSeparator className="bg-[#2A2A2A]"/>
              <DropdownMenuItem onClick={() => open('delete')} data-testid="action-delete" className="gap-2 cursor-pointer text-[#FF8A8A]">
                <Trash2 size={14}/> Delete (soft)
              </DropdownMenuItem>
            </>
          )}
          {/* Hard delete — super-admin only, also for tombstones (purges the soft-deleted row) */}
          {isSuperAdmin && (
            <DropdownMenuItem onClick={() => open('hardDelete')} data-testid="action-hard-delete" className="gap-2 cursor-pointer text-[#FF4D4D]">
              <Trash2 size={14}/> Permanently delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Confirmation dialog with optional reason field */}
      <AlertDialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <AlertDialogContent
          data-testid="customer-action-confirm"
          className="bg-[#0F0F0F] border border-[#2A2A2A] text-[#F2F2F2]"
          onClick={e => e.stopPropagation()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#F2F2F2]">
              {dialog?.danger && <AlertTriangle size={16} className="text-[#FF4D4D]"/>}
              {dialog?.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#B9B9B9]">
              {dialog?.desc}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2">
            <Label className="text-[#B9B9B9] text-xs">
              {dialog?.requireReason ? 'Reason (required)' : 'Reason (optional)'}
            </Label>
            <Textarea
              data-testid="customer-action-reason"
              placeholder="Add a note for the audit log…"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={2}
              className="bg-[#111111] border-[#2A2A2A] text-sm"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="customer-action-cancel"
              className="bg-transparent border-[#2A2A2A] text-[#B9B9B9] hover:bg-[#1A1A1A] hover:text-[#F2F2F2]"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="customer-action-confirm-btn"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); run(); }}
              className={dialog?.danger
                ? 'bg-[#7A1A1A] hover:bg-[#A02020] text-white gap-2'
                : 'bg-[#D4A843] hover:bg-[#C39633] text-black gap-2'}
            >
              {busy && <Loader2 size={14} className="animate-spin"/>}
              {dialog?.danger ? 'Confirm' : 'Apply'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
